import { getExperimentValue } from '@canvas/client-feature-flags';
import { ReliabilityMetrics, type CanvasMetadata } from '@canvas/frontend-metrics';

import { reliabilityExperimentDimensions } from './reliability-experiment-dimensions';

type ExperimentSnapshot = Record<string, Record<string, unknown>>;

let cachedReliabilityExperimentMetadata: CanvasMetadata | undefined;
let isReliabilityExperimentMetadataRegistered = false;

function isReliabilityMetadataValue(value: unknown): value is string | number | boolean {
  return (
    typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
  );
}

function readRegisteredReliabilityExperimentDimensions(): ExperimentSnapshot {
  const experimentValues: ExperimentSnapshot = {};

  reliabilityExperimentDimensions.forEach(({ experimentName, paramName }) => {
    const paramKey = String(paramName);

    experimentValues[experimentName] = {
      [paramKey]: getExperimentValue(experimentName, paramName, {
        fireExperimentExposure: false
      })
    };
  });

  return experimentValues;
}

/**
 * Builds the small metadata object merged into each Databases Reliability
 * metric.
 *
 * The output intentionally contains only top-level `exp_<experimentName>`
 * fields. An opaque `experimentStateId` is not emitted because it cannot answer
 * "which experiment changed?" when many experiments are enabled.
 */
export function buildReliabilityExperimentMetadata(
  experimentValues: ExperimentSnapshot = readRegisteredReliabilityExperimentDimensions()
): CanvasMetadata {
  const metadata: CanvasMetadata = {};

  reliabilityExperimentDimensions.forEach(
    ({ experimentName, paramName, deriveValue }) => {
      const paramKey = String(paramName);
      const rawValue = experimentValues[experimentName]?.[paramKey];
      const value =
        rawValue !== undefined && deriveValue ? deriveValue(rawValue) : rawValue;

      if (isReliabilityMetadataValue(value)) {
        metadata[`exp_${experimentName}`] = value;
      }
    }
  );

  return metadata;
}

/**
 * Returns the cached Reliability experiment metadata for tests and diagnostics.
 * Production metric publication should only use the registered transformer.
 */
export function getCachedReliabilityExperimentMetadata(): CanvasMetadata | undefined {
  return cachedReliabilityExperimentMetadata;
}

/**
 * Reads registered experiment values once for the current page-load lifecycle.
 */
export function initializeReliabilityExperimentMetadata(): CanvasMetadata {
  cachedReliabilityExperimentMetadata = buildReliabilityExperimentMetadata();

  return cachedReliabilityExperimentMetadata;
}

/**
 * Registers one global Reliability metadata transformer for Databases bootstrap.
 * The transformer only merges the cached object so hot metric paths do not read
 * experiments or compute derived metadata. Registration is best-effort because
 * missing experiment dimensions should never block Databases startup.
 */
export function registerReliabilityExperimentMetadata(): void {
  if (isReliabilityExperimentMetadataRegistered) {
    return;
  }

  try {
    initializeReliabilityExperimentMetadata();

    ReliabilityMetrics.getInstance().addCustomMetadataTransformer((metadata) => ({
      ...metadata,
      ...cachedReliabilityExperimentMetadata
    }));

    isReliabilityExperimentMetadataRegistered = true;
  } catch (error) {
    console.error('Unable to register Databases Reliability experiment metadata', error);
  }
}

/**
 * Clears module-level state between unit tests.
 */
export function resetReliabilityExperimentMetadataForTests(): void {
  cachedReliabilityExperimentMetadata = undefined;
  isReliabilityExperimentMetadataRegistered = false;
}
